import { SalesOpportunity } from '../../services/salesOpportunitiesApi';
import { getStageConfig, OPPORTUNITY_STAGES, OpportunityStageId } from '../../types/OpportunityStage';

type OpportunityKanbanBoardProps = {
  items: SalesOpportunity[];
  onSelect: (opportunity: SalesOpportunity) => void;
  onMove: (opportunityId: string, stage: OpportunityStageId, sortOrder: number) => void;
};

function formatCurrency(value: number): string {
  return value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function OpportunityKanbanBoard({ items, onSelect, onMove }: OpportunityKanbanBoardProps) {
  function handleDrop(stage: OpportunityStageId, event: React.DragEvent<HTMLDivElement>): void {
    event.preventDefault();
    const opportunityId = event.dataTransfer.getData('text/opportunity-id');
    if (!opportunityId) return;

    const columnItems = items.filter((item) => item.stage === stage);
    onMove(opportunityId, stage, columnItems.length);
  }

  return (
    <div className="flex gap-4 overflow-x-auto pb-2">
      {OPPORTUNITY_STAGES.map((stage) => {
        const columnItems = items
          .filter((item) => item.stage === stage.id)
          .sort((left, right) => left.sortOrder - right.sortOrder);
        const columnTotal = columnItems.reduce((sum, item) => sum + item.amount, 0);

        return (
          <div
            key={stage.id}
            className={`flex min-w-[280px] max-w-[320px] flex-1 flex-col rounded-xl border ${stage.columnClass}`}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => handleDrop(stage.id, event)}
          >
            <div className={`rounded-t-xl px-3 py-2 ${stage.headerClass}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold">{stage.label}</span>
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs">{columnItems.length}</span>
              </div>
              <p className="mt-1 text-xs opacity-90">{formatCurrency(columnTotal)}</p>
            </div>

            <div className="flex flex-1 flex-col gap-2 p-2 min-h-[120px]">
              {columnItems.map((opportunity) => (
                <button
                  key={opportunity.id}
                  type="button"
                  draggable
                  onDragStart={(event) => {
                    event.dataTransfer.setData('text/opportunity-id', opportunity.id);
                  }}
                  onClick={() => onSelect(opportunity)}
                  className={`erp-card w-full border-l-4 p-3 text-left shadow-sm transition hover:shadow-md ${stage.cardAccentClass}`}
                >
                  <p className="font-semibold text-secondary-900 line-clamp-2">{opportunity.title}</p>
                  {opportunity.companyName || opportunity.clientName || opportunity.prospectCompany ? (
                    <p className="mt-1 text-xs text-secondary-600 line-clamp-1">
                      {opportunity.companyName
                        ?? opportunity.clientName
                        ?? opportunity.prospectCompany}
                    </p>
                  ) : null}
                  <p className="mt-2 text-sm font-bold text-primary-700">{formatCurrency(opportunity.amount)}</p>
                  <div className="mt-2 flex flex-wrap gap-2 text-xs text-secondary-500">
                    {opportunity.engagementStatus && opportunity.engagementStatus !== 'Active' ? (
                      <span className="rounded bg-secondary-100 px-1.5 py-0.5 text-secondary-700">
                        {opportunity.engagementStatus === 'Paused'
                          ? 'Pausada'
                          : 'Sem contato'}
                      </span>
                    ) : null}
                    {opportunity.ownerUserName ? <span>{opportunity.ownerUserName}</span> : null}
                    {opportunity.probabilityPercent > 0 ? (
                      <span>{opportunity.probabilityPercent}%</span>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function formatOpportunityStageLabel(stage: string): string {
  return getStageConfig(stage).label;
}
